// ============================================================
// ORIVA AI — POLICY CONTENT
// Solid placeholder legal copy. Edit freely — business name and
// support email are pulled automatically from config/siteConfig.js.
// ============================================================

import { siteConfig } from "@/config/siteConfig";

const email = siteConfig.contact.supportEmail;
const biz = siteConfig.contact.businessName;

export const policies = {
  privacy: {
    title: "Privacy Policy",
    updated: "Last updated: July 2026",
    intro: `This Privacy Policy explains what information ${biz} ("ORIVA", "we", "us") collects when you use our website and mobile application, and how we use it.`,
    sections: [
      {
        heading: "Information we collect",
        paragraphs: [
          "Account information: when you create an account in the ORIVA app, we collect the details you provide, such as your name and email address.",
          "Content you submit: prompts, questions, documents, images and other content you enter into the app are processed to provide the AI features you request.",
          "Usage data: we collect basic technical information such as device type, app version, feature usage and crash logs to keep the service reliable and improve it.",
          "Support communications: if you contact us, we keep a record of the conversation so we can respond and follow up.",
          "Uploaded files: documents, images and other files you upload for analysis are processed to fulfil your request and are associated with your account until you delete them or your account.",
        ],
      },
      {
        heading: "Credits and subscriptions",
        paragraphs: [
          "ORIVA uses a credits system for AI actions. We record your credit balance and usage history to operate the system, show you costs upfront and enforce plan limits.",
          "Subscriptions are billed through Google Play. We store your plan status and renewal state — never your full payment card details.",
        ],
      },
      {
        heading: "How we use your information",
        paragraphs: [
          "We use the information above to operate and improve the ORIVA app, provide customer support, process subscriptions, and keep the service secure.",
          "We do not sell your personal data, and we do not use your private content to advertise to you.",
        ],
      },
      {
        heading: "Third-party processors",
        paragraphs: [
          "ORIVA relies on trusted third parties to operate: AI model providers (to generate responses), Google Play (for app distribution and subscription billing) and cloud hosting providers. These parties process data only as needed to provide their services to us.",
          "Payments are handled by the app store. We do not see or store your full payment card details.",
        ],
      },
      {
        heading: "Data retention",
        paragraphs: [
          "We keep your information for as long as your account is active or as needed to provide the service. You can request deletion of your account and associated data at any time — see our Account Deletion page.",
        ],
      },
      {
        heading: "Your rights",
        paragraphs: [
          "You may request access to, correction of, or deletion of your personal data. You may also object to certain processing. To exercise any of these rights, contact us at the address below.",
        ],
      },
      {
        heading: "Children",
        paragraphs: [
          "ORIVA is not directed at children under the age required by applicable law to consent to data processing. If you believe a child has provided us personal data, contact us and we will remove it.",
        ],
      },
      {
        heading: "Changes to this policy",
        paragraphs: [
          "We may update this policy from time to time. The date at the top shows the latest revision; significant changes will be signposted in the app or on this page.",
        ],
      },
      {
        heading: "Contact",
        paragraphs: [`Questions about privacy? Email us at ${email}.`],
      },
    ],
  },

  terms: {
    title: "Terms of Service",
    updated: "Last updated: July 2026",
    intro: `These Terms of Service govern your use of the ${biz} website and mobile application. By using ORIVA, you agree to these terms.`,
    sections: [
      {
        heading: "The service",
        paragraphs: [
          "ORIVA is an AI-powered mobile application that provides a universal AI assistant together with specialised tools for studying, content creation and professional work. Features and limits depend on your subscription plan and may change as the product evolves.",
        ],
      },
      {
        heading: "Accounts",
        paragraphs: [
          "You are responsible for keeping your account credentials confidential and for all activity under your account. You must provide accurate information when registering.",
        ],
      },
      {
        heading: "Subscriptions, billing and credits",
        paragraphs: [
          "Paid plans are billed monthly in Indian Rupees (INR) through Google Play. Subscription management, renewal and cancellation are handled through your store account settings. Prices are shown before purchase; applicable taxes may apply.",
          "ORIVA uses a credits system: AI actions spend credits, and the cost of an action is shown in the app before you confirm it. Credits are linked to your account and plan, have no cash value and are not transferable.",
        ],
      },
      {
        heading: "Your content and uploads",
        paragraphs: [
          "You retain ownership of the prompts, documents, images and other content you upload or create with ORIVA. You grant us the limited rights needed to process that content to provide the service.",
          "Do not upload content you have no right to share, or content that is unlawful, harmful or infringing. We may remove content and suspend accounts that violate these rules.",
        ],
      },
      {
        heading: "Acceptable use",
        paragraphs: [
          "You agree not to use ORIVA to generate unlawful, harmful, deceptive or infringing content; to attempt to disrupt or reverse-engineer the service; or to resell access without permission. We may suspend accounts that violate these rules.",
        ],
      },
      {
        heading: "AI-generated content",
        paragraphs: [
          "Responses, images and analyses produced by ORIVA are generated by artificial intelligence. They may be inaccurate, incomplete or outdated, and do not constitute professional advice of any kind. See our AI Disclaimer for details.",
        ],
      },
      {
        heading: "Intellectual property",
        paragraphs: [
          "The ORIVA name, logo, app and website are the property of ORIVA. You retain ownership of the content you create using the app, subject to the rights needed for us to operate the service.",
        ],
      },
      {
        heading: "Limitation of liability",
        paragraphs: [
          "The service is provided “as is” without warranties of any kind. To the maximum extent permitted by law, ORIVA is not liable for indirect or consequential damages arising from your use of the service, including decisions made based on AI-generated output.",
        ],
      },
      {
        heading: "Governing law and changes",
        paragraphs: [
          "These terms are governed by the laws of India. We may update these terms from time to time; continued use of the service after changes take effect constitutes acceptance.",
        ],
      },
      {
        heading: "Contact",
        paragraphs: [`Questions about these terms? Email us at ${email}.`],
      },
    ],
  },

  refund: {
    title: "Refund & Cancellation Policy",
    updated: "Last updated: July 2026",
    intro: `This policy explains how cancellations and refunds work for ${biz} subscriptions.`,
    sections: [
      {
        heading: "How subscriptions work",
        paragraphs: [
          "ORIVA offers monthly subscription plans (Standard, Middle, Ultimate and Ultimate Pro) billed in Indian Rupees (INR) through the app store. Your subscription renews automatically each month until cancelled.",
        ],
      },
      {
        heading: "How to cancel",
        paragraphs: [
          "You can cancel at any time from your Google Play account: open the Play Store, tap your profile, go to Payments & subscriptions → Subscriptions, select ORIVA and choose Cancel. Cancellation takes effect at the end of the current billing period — you keep access until then, and no further charges are made.",
        ],
      },
      {
        heading: "Refunds",
        paragraphs: [
          "Purchases made through Google Play are subject to Google Play's refund policies. If you believe you were charged in error, you can request a refund through your Google Play order history, or contact us and we will help where we can.",
          "Refund requests are reviewed case by case. We do not promise refunds for partially used billing periods or partially used credits, but we will always assess genuine billing errors, duplicate charges and unauthorised purchases fairly.",
        ],
      },
      {
        heading: "Contact",
        paragraphs: [
          `For billing questions, email ${email} with the email address linked to your account and, if possible, your store order number (found in your purchase receipt).`,
        ],
      },
    ],
  },

  "account-deletion": {
    title: "Account Deletion",
    updated: "Last updated: July 2026",
    intro: `You can delete your ${biz} account and associated personal data at any time. This page explains how.`,
    sections: [
      {
        heading: "How to request deletion",
        paragraphs: [
          "Option 1 — in the app: where available, go to Settings → Account → Delete account and follow the prompts.",
          `Option 2 — by email: send a request to ${email} from the email address linked to your account with the subject “Account deletion”. We may ask you to confirm the request before proceeding.`,
        ],
      },
      {
        heading: "What gets deleted",
        paragraphs: [
          "When your deletion request is completed, we delete your account profile and the content associated with your account (chats, notes and generated content), and you will no longer be able to sign in.",
          "We aim to complete deletion within 30 days of a confirmed request.",
        ],
      },
      {
        heading: "What may be retained",
        paragraphs: [
          "Some records may be retained where required for legal, tax, fraud-prevention or accounting purposes — for example, transaction records relating to purchases. These are kept only as long as legally necessary and are not used for any other purpose.",
        ],
      },
      {
        heading: "Subscriptions",
        paragraphs: [
          "Deleting your account does not automatically cancel an active store subscription. Please cancel your subscription in your Google Play account settings first to avoid further charges.",
        ],
      },
      {
        heading: "Contact",
        paragraphs: [`Questions about account deletion? Email us at ${email}.`],
      },
    ],
  },

  "ai-disclaimer": {
    title: "AI Disclaimer",
    updated: "Last updated: July 2026",
    intro: `${biz} is built on artificial intelligence. Please read this page to understand what that means for you.`,
    sections: [
      {
        heading: "AI-generated output",
        paragraphs: [
          "All responses, summaries, images, analyses and other content produced by ORIVA are generated by artificial-intelligence models. No human reviews or writes the individual answers you receive.",
        ],
      },
      {
        heading: "Accuracy is not guaranteed",
        paragraphs: [
          "AI systems can and do make mistakes. Output may be inaccurate, incomplete, outdated or unsuitable for your situation. Always independently verify important information — especially anything academic, medical, legal, financial or safety-related — with a qualified professional or authoritative source before relying on it.",
        ],
      },
      {
        heading: "Not professional advice",
        paragraphs: [
          "Nothing produced by ORIVA constitutes medical, legal, financial, tax, educational or any other form of professional advice. Tools such as Business Planning, Data Analysis and SEO Optimizer provide starting points, not guarantees of outcomes, rankings or results.",
        ],
      },
      {
        heading: "Your responsibility",
        paragraphs: [
          "You are responsible for how you use AI-generated content, including checking it for accuracy, originality and compliance with the rules of any platform, institution or jurisdiction where you publish or submit it.",
        ],
      },
      {
        heading: "Questions",
        paragraphs: [`If anything about how ORIVA's AI works is unclear, email us at ${email}.`],
      },
    ],
  },
};

export const policyOrder = [
  { slug: "privacy", path: "/privacy-policy", label: "Privacy Policy" },
  { slug: "terms", path: "/terms", label: "Terms of Service" },
  { slug: "refund", path: "/refund-policy", label: "Refund & Cancellation" },
  { slug: "account-deletion", path: "/account-deletion", label: "Account Deletion" },
  { slug: "ai-disclaimer", path: "/ai-disclaimer", label: "AI Disclaimer" },
];
